import posthog from "posthog-js";

import { PatientAuthType } from "@/types/enums/patient-auth-type";

import {
  SignUpSource,
  resolveSignUpSource,
} from "./sign-up/sign-up-attribution";
import { AuthMethodId, AuthSurface } from "./types";

// Session-scoped only: neither this marker nor the events contain identity or
// contact information. PostHog supplies the same tenant/session to both events.
const FAILURE_KEY = "decoda:auth:password-failure-session";
let storageBlockedScope: string | null = null;

function scope(): { key: string; session: string; value: string } | null {
  if (typeof window === "undefined") return null;
  let session: string | undefined;
  try {
    session = posthog.get_session_id?.();
  } catch {
    return null;
  }
  const tenant = window.location.pathname.split("/")[1]?.replaceAll("-", "_");
  if (!session || !tenant) return null;
  const key = `${FAILURE_KEY}:${tenant}`;
  return { key, session, value: `${key}:${session}` };
}

function markFailure(): void {
  const current = scope();
  if (!current) return;
  try {
    window.localStorage.setItem(current.key, current.session);
    storageBlockedScope = null;
  } catch {
    storageBlockedScope = current.value;
  }
}

function consumeFailure(): boolean {
  const current = scope();
  if (!current) return false;
  try {
    if (window.localStorage.getItem(current.key) !== current.session)
      return false;
    window.localStorage.removeItem(current.key);
    return true;
  } catch {
    const matched = storageBlockedScope === current.value;
    if (matched) storageBlockedScope = null;
    return matched;
  }
}

function capture(name: string, properties: Record<string, unknown>): void {
  try {
    posthog.capture(name, properties);
  } catch {
    // Observability must not change authentication or routing.
  }
}

export function recordRejectedPassword(
  surface: AuthSurface,
  googleHintShown: boolean,
): void {
  markFailure();
  capture("patient_password_sign_in_failed", {
    surface,
    google_hint_shown: googleHintShown,
  });
}

function recordResolvedAuth(
  surface: AuthSurface,
  method: AuthMethodId,
  patientType: PatientAuthType,
  signupSource?: SignUpSource,
): void {
  if (surface === AuthSurface.PROVIDER_LOGIN || surface === AuthSurface.INVITE)
    return;
  const isSignUp = surface === AuthSurface.PATIENT_SIGN_UP;
  capture(isSignUp ? "patient_account_created" : "patient_auth_resolved", {
    surface,
    auth_method: method === AuthMethodId.PHONE ? "mfa_phone" : "portal_account",
    credential_kind: method,
    patient_type: patientType,
    ...(isSignUp ? { signup_source: resolveSignUpSource(signupSource) } : {}),
  });
}

/** Call only at an existing branch where the patient can use this clinic. */
export function recordPatientAccess(
  surface: AuthSurface,
  method: AuthMethodId,
  patientType: PatientAuthType = PatientAuthType.EXISTING,
): void {
  recordResolvedAuth(surface, method, patientType);
  capture("patient_auth_access_confirmed", {
    surface,
    credential_kind: method,
    patient_type: patientType,
    after_password_failure: consumeFailure(),
  });
}

/** Completing sign-up consumes a failed-sign-in cohort, not a sign-in recovery. */
export function recordCompletedSignUp(
  surface: AuthSurface,
  signupSource?: SignUpSource,
  patientType: PatientAuthType = PatientAuthType.EXISTING,
  method: AuthMethodId = AuthMethodId.PASSWORD,
): void {
  recordResolvedAuth(surface, method, patientType, signupSource);
  capture("patient_sign_up_completed", {
    surface,
    signup_source: resolveSignUpSource(signupSource),
    patient_type: patientType,
    after_password_failure: consumeFailure(),
  });
}
